import React, { useEffect, useState } from 'react';
import { Form, Space, Typography } from 'antd';
import PromoTable from '../common/PromoTable';
import PropTypes from 'prop-types';
import PromoTransactionsSearch from './PromoTransactionsSearch';
import { getParams } from '../../../../utilities/filterConstructor';
import { getCourseOffers } from '../../../../services/courseOfferService';
import { CSVLink } from 'react-csv';
import {
  formatPromoTransactionsCsvData,
  getPromoStatusLabelByType,
} from '../../../../utilities/formatData';

const { Title } = Typography;
const COURSE_LIMIT = 1000;

const PromoTransactions = ({ getTransactions, type }) => {
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [offset, setOffset] = useState((current - 1) * pageSize);

  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(false);
  const [currentParams, setCurrentParams] = useState({});
  const [fetching, setFetching] = useState(false);

  const [courseOffers, setCourseOffers] = useState([]);
  const [fetchingSearchData, setFetchingSearchData] = useState(false);
  const [csvReport, setCsvReport] = useState(null);

  const [status, setStatus] = useState({
    type: 'IDLE',
    message: 'In idle mode',
  });

  const [form] = Form.useForm();

  const columns = [
    {
      title: 'No',
      dataIndex: 'index',
      key: 'index',
      render: (value, item, index) => <span>{offset + index + 1}</span>,
    },
    {
      title: 'Promo Code',
      dataIndex: 'promoId',
      key: 'promoId',
      render: (value) => <span>{value?.code}</span>,
    },
    {
      title: 'Transactor',
      dataIndex: 'transactorId',
      key: 'transactorId',
      render: (value) => <span>{value?.email}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (value) => <span>{value}</span>,
    },
    {
      title: 'Discount',
      dataIndex: 'promoId',
      key: 'promoId',
      render: (value) => (
        <span>{`${
          value.discountType === 'amount_off'
            ? value?.discountValue / 100
            : value?.discountValue
        } (${value.discountType})`}</span>
      ),
    },
    {
      title: 'Discount Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (value) => <span>{value / 100}</span>,
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      render: (value) => <span>{value / 100}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, { type }) => (
        <span>{getPromoStatusLabelByType(type, status)}</span>
      ),
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value) => <span>{new Date(value).toLocaleString()}</span>,
    },
  ];

  const getData = async (
    newParams,
    limit = pageSize,
    currentPage = current
  ) => {
    const params = newParams ? newParams : currentParams;
    setFetching(true);
    await getTransactions(currentPage, limit, params)
      .then((res) => {
        if (!res.error) {
          if (type === 'withoutCode') {
            setTransactions(res.data.paginatedList);
            setTotalCount(res.data.totalCount);
          } else {
            setTransactions(res.data.transactions.paginatedList);
            setTotalCount(res.data.transactions.totalCount);
          }
        } else {
          setError(res.error);
        }
        setFetching(false);
      })
      .catch((e) => {
        console.log(e);
        setError(e);
        setFetching(false);
      });
  };

  const onPaginationChange = (data) => {
    getData(currentParams, data.pageSize, data.current);
    setCurrent(data.current);
    setPageSize(data.pageSize);
    setOffset((data.current - 1) * data.pageSize);
  };

  const onSearch = (data) => {
    const params = getParams(data);
    setCurrentParams(params);
    setCurrent(1);
  };

  const onReset = () => {
    form.resetFields();
    setOffset(0);
    setCurrent(1);
    getData({}, pageSize, 1).then();
  };

  const fetchCourseOffer = () => {
    setFetchingSearchData(true);
    getCourseOffers(1, COURSE_LIMIT, {})
      .then((response) => {
        const data = response.data.paginatedList.map((offer) => ({
          label: offer.code,
          value: offer._id,
        }));
        setCourseOffers(data);
        setFetchingSearchData(false);
      })
      .catch((error) => {
        if (error?.response?.data?.message)
          setStatus({ type: 'ERROR', message: error.response.data.message });
        else
          setStatus({
            type: 'ERROR',
            message: 'Getting questions from the backend failed!',
          });
      });
  };

  useEffect(() => {
    getData().then();
    if (type === 'withoutCode') {
      fetchCourseOffer();
    }
  }, [current, currentParams]);

  useEffect(() => {
    if (!csvReport && totalCount) {
      const fetchCsvData = async () => {
        try {
          const res = await getTransactions(1, totalCount, {});
          const transactionsData =
            type === 'withoutCode'
              ? res?.data?.paginatedList
              : res?.data?.transactions?.paginatedList;

          const formattedCsvData = formatPromoTransactionsCsvData(
            transactionsData
          );
          setCsvReport(formattedCsvData);
        } catch (e) {
          setCsvReport(null);
        }
      };

      fetchCsvData();
    }
  }, [totalCount]);

  return (
    <div>
      <div>
        <Space size="large">
          <Title level={3}>Promo Transactions</Title>
        </Space>
        <section>
          {type === 'withoutCode' && (
            <PromoTransactionsSearch
              onReset={onReset}
              onSearch={onSearch}
              form={form}
              status={status}
              setStatus={setStatus}
              courseOffers={courseOffers}
            />
          )}
          {csvReport?.data && csvReport?.data?.length > 0 ? (
            <CSVLink
              data={csvReport.data}
              headers={csvReport.headers}
              filename={csvReport.filename}
            >
              Export to CSV
            </CSVLink>
          ) : null}
          <PromoTable
            data={transactions}
            onPaginationChange={onPaginationChange}
            current={current}
            totalCount={totalCount}
            offset={offset}
            fetching={fetching}
            columns={columns}
          />
        </section>
      </div>
    </div>
  );
};

PromoTransactions.defaultProps = {
  type: 'withoutCode',
};

PromoTransactions.propTypes = {
  getTransactions: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};

export default PromoTransactions;
